"use strict";
/**
 * Outline body model: nested markdown-style lists in pnBody with fold markers.
 *
 * Fold convention: trailing " (+)" on a bullet line means collapsed (children hidden in map).
 * Expanded bullets have no fold suffix.
 *
 * Classic example:
 *   Sprinto review
 *
 *   - Infra entity should be classified (+)
 *     - csms-queue...
 *       - [ ] ask Team
 *   - Risks
 *     - still need to export list [ ]
 *
 * Loose outline (blank-separated L1, undashed children) is also accepted:
 *   Client XYZ
 *
 *   not the funnest
 *   power through quickly
 *
 *   Partner portal
 *   - form
 *   - file upload
 *
 * Map structure writes normalize free-form to classic `- ` lists.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.toggleCheckbox = exports.deleteBulletIfEmpty = exports.moveBulletDown = exports.moveBulletUp = exports.bulletBlockRange = exports.outdentBullet = exports.indentBullet = exports.addChildUnder = exports.addSiblingAfter = exports.splitBulletLabelIntoPeers = exports.PEER_SPLIT_MARKER = exports.updateBulletFromEdit = exports.parseOutlineEditDraft = exports.normalizeOutlineEditDraft = exports.updateLabel = exports.setExpandToPresentationLevel = exports.setExpandToMaxDepth = exports.setAllCollapsed = exports.setCollapsed = exports.toggleCollapse = exports.flattenVisible = exports.expandAncestorsOf = exports.bulletIdAtSourceLine = exports.buildOutlineForest = exports.serializeOutlineBody = exports.parseOutlineBody = exports.hasOutlineStructure = exports.hasOutlineBullets = exports.resetOutlineIds = void 0;
var FOLD_SUFFIX = ' (+)';
var FOLD_RE = /\s*\(\+\)$/;
var BULLET_RE = /^( *)-\s+(.*)$/;
var CHECKBOX_RE = /^\[([ xX])\]\s+(.*)$/;
var idSeq = 0;
function nextId(prefix) {
    idSeq += 1;
    return "".concat(prefix, "-").concat(idSeq);
}
/** Reset id counter (tests). */
function resetOutlineIds() {
    idSeq = 0;
}
exports.resetOutlineIds = resetOutlineIds;
function hasOutlineBullets(body) {
    return (body || '').split(/\r\n?|\n/).some(function (line) { return BULLET_RE.test(line); });
}
exports.hasOutlineBullets = hasOutlineBullets;
/** True when body yields at least one map bullet (classic `- ` or loose free-form). */
function hasOutlineStructure(body) {
    if (!(body || '').trim())
        return false;
    if (hasOutlineBullets(body))
        return true;
    return parseOutlineBody(body).lines.some(function (l) { return l.kind === 'bullet'; });
}
exports.hasOutlineStructure = hasOutlineStructure;
function parseBulletLabel(rest) {
    var collapsed = false;
    var work = rest;
    if (FOLD_RE.test(work)) {
        collapsed = true;
        work = work.replace(FOLD_RE, '').replace(/\s+$/, '');
    }
    var cb = CHECKBOX_RE.exec(work);
    if (cb) {
        var mark = cb[1];
        return {
            label: cb[2],
            collapsed: collapsed,
            checkbox: mark === 'x' || mark === 'X' ? 'done' : 'open',
        };
    }
    return { label: work, collapsed: collapsed, checkbox: null };
}
function makeBullet(depth, label, collapsed, checkbox) {
    return {
        kind: 'bullet',
        id: nextId('n'),
        depth: depth,
        label: label,
        collapsed: collapsed,
        checkbox: checkbox,
    };
}
/** Classify raw string lines without loose promotion. */
function classifyLines(raw) {
    var lines = [];
    for (var _i = 0, raw_1 = raw; _i < raw_1.length; _i++) {
        var line = raw_1[_i];
        if (line === '') {
            lines.push({ kind: 'blank', id: nextId('b') });
            continue;
        }
        var m = BULLET_RE.exec(line);
        if (m) {
            var spaces = m[1].length;
            var depth = Math.floor(spaces / 2);
            var parsed = parseBulletLabel(m[2]);
            lines.push(makeBullet(depth, parsed.label, parsed.collapsed, parsed.checkbox));
            continue;
        }
        lines.push({ kind: 'text', id: nextId('t'), text: line });
    }
    return lines;
}
function parseLooseContentLine(raw) {
    var m = BULLET_RE.exec(raw);
    if (m) {
        var parsed = parseBulletLabel(m[2]);
        return {
            kind: 'bullet',
            spaces: m[1].length,
            label: parsed.label.replace(/\s+$/, ''),
            collapsed: parsed.collapsed,
            checkbox: parsed.checkbox,
        };
    }
    return { kind: 'text', label: raw.replace(/^\s+/, '').replace(/\s+$/, '') };
}
/**
 * Optional document title: a single text line, blank, then a body that looks like an
 * outline (multi-line block and/or `- ` lines). Blank-separated single lines alone
 * are all L1 nodes — not title + one L1.
 *
 * Returns index of first body line in the classified/raw arrays, or 0 if no heading.
 */
function findLooseBodyStart(raw) {
    var i = 0;
    while (i < raw.length && raw[i] === '')
        i += 1;
    if (i >= raw.length)
        return 0;
    // Heading must be exactly one non-empty non-bullet line
    if (BULLET_RE.test(raw[i]))
        return 0;
    var afterTitle = i + 1;
    if (afterTitle >= raw.length)
        return 0;
    if (raw[afterTitle] !== '')
        return 0; // no blank after title
    var k = afterTitle;
    while (k < raw.length && raw[k] === '')
        k += 1;
    if (k >= raw.length)
        return 0;
    var bodyRaw = raw.slice(k);
    // Body must look like outline structure, not another lone L1 sibling
    var hasDash = false;
    var hasMultiLineBlock = false;
    var blockLen = 0;
    var nonEmptyBodyLines = 0;
    for (var _i = 0, bodyRaw_1 = bodyRaw; _i < bodyRaw_1.length; _i++) {
        var line = bodyRaw_1[_i];
        if (line === '') {
            if (blockLen >= 2)
                hasMultiLineBlock = true;
            blockLen = 0;
            continue;
        }
        nonEmptyBodyLines += 1;
        blockLen += 1;
        if (BULLET_RE.test(line))
            hasDash = true;
    }
    if (blockLen >= 2)
        hasMultiLineBlock = true;
    if (!hasDash && !hasMultiLineBlock) {
        // e.g. "A\n\nB\n\nC" — all L1, no title
        return 0;
    }
    // Title + structured body (e.g. Client XYZ sample)
    if (nonEmptyBodyLines < 1)
        return 0;
    return k;
}
/** After optional classic preamble, every non-blank line is already a bullet. */
function isClassicBulletBody(lines) {
    var i = 0;
    while (i < lines.length && lines[i].kind === 'blank')
        i += 1;
    // Optional classic preamble: text lines then blank then all bullets
    var j = i;
    while (j < lines.length && lines[j].kind === 'text')
        j += 1;
    if (j > i) {
        if (j >= lines.length || lines[j].kind !== 'blank') {
            // text without blank separator — not pure classic list
            // unless there are no bullets at all
        }
        else {
            while (j < lines.length && lines[j].kind === 'blank')
                j += 1;
            i = j;
        }
    }
    var sawBullet = false;
    for (var k = i; k < lines.length; k++) {
        var l = lines[k];
        if (l.kind === 'blank')
            continue;
        if (l.kind === 'text')
            return false;
        if (l.kind === 'bullet')
            sawBullet = true;
    }
    return sawBullet;
}
/**
 * Promote free-form blocks into bullets:
 * - blank lines separate L1 blocks
 * - first line of each block → depth 0
 * - following undashed lines → depth 1
 * - following dashed lines → depth relative to shallowest dash indent in the block (+1 under L1)
 */
function promoteLooseOutline(raw) {
    var bodyStart = findLooseBodyStart(raw);
    var out = [];
    // Preserve heading text + blanks before body as text/blank (preamble)
    for (var i = 0; i < bodyStart; i++) {
        if (raw[i] === '') {
            out.push({ kind: 'blank', id: nextId('b') });
        }
        else if (!BULLET_RE.test(raw[i])) {
            out.push({ kind: 'text', id: nextId('t'), text: raw[i] });
        }
    }
    var rawIdx = bodyStart;
    while (rawIdx < raw.length) {
        if (raw[rawIdx] === '') {
            out.push({ kind: 'blank', id: nextId('b') });
            rawIdx += 1;
            continue;
        }
        var blockRaw = [];
        while (rawIdx < raw.length && raw[rawIdx] !== '') {
            blockRaw.push(raw[rawIdx]);
            rawIdx += 1;
        }
        if (!blockRaw.length)
            continue;
        var contents = blockRaw.map(parseLooseContentLine);
        var first = contents[0];
        out.push(makeBullet(0, first.label, first.kind === 'bullet' ? first.collapsed : false, first.kind === 'bullet' ? first.checkbox : null));
        if (contents.length === 1)
            continue;
        var rest = contents.slice(1);
        var dashed = rest.filter(function (c) { return c.kind === 'bullet'; });
        var minRaw = dashed.length
            ? Math.min.apply(Math, dashed.map(function (d) { return Math.floor(d.spaces / 2); })) : 0;
        for (var _i = 0, rest_1 = rest; _i < rest_1.length; _i++) {
            var c = rest_1[_i];
            if (c.kind === 'text') {
                out.push(makeBullet(1, c.label, false, null));
            }
            else {
                var rawDepth = Math.floor(c.spaces / 2);
                var depth = rawDepth - minRaw + 1;
                out.push(makeBullet(depth, c.label, c.collapsed, c.checkbox));
            }
        }
    }
    return out;
}
function shouldPromoteLoose(raw, linesClassic) {
    if (isClassicBulletBody(linesClassic))
        return false;
    var nonBlank = raw.filter(function (l) { return l !== ''; });
    if (nonBlank.length === 0)
        return false;
    // Plain single-line note
    if (nonBlank.length === 1 && !BULLET_RE.test(nonBlank[0]))
        return false;
    // Any free-text line, or blank-separated multi-block free-form
    var hasText = linesClassic.some(function (l) { return l.kind === 'text'; });
    var hasBullet = linesClassic.some(function (l) { return l.kind === 'bullet'; });
    if (hasText && (nonBlank.length >= 2 || hasBullet))
        return true;
    // Blank-separated single-line L1s only (all text, ≥2 blocks)
    if (hasText && !hasBullet) {
        var blocks = 0;
        var inBlock = false;
        for (var _i = 0, raw_2 = raw; _i < raw_2.length; _i++) {
            var line = raw_2[_i];
            if (line === '') {
                inBlock = false;
            }
            else if (!inBlock) {
                blocks += 1;
                inBlock = true;
            }
        }
        return blocks >= 2 || nonBlank.length >= 2;
    }
    return false;
}
function parseOutlineBody(body) {
    var raw = (body || '').replace(/\r\n?/g, '\n').split('\n');
    var linesClassic = classifyLines(raw);
    if (!shouldPromoteLoose(raw, linesClassic)) {
        return { lines: linesClassic };
    }
    return { lines: promoteLooseOutline(raw) };
}
exports.parseOutlineBody = parseOutlineBody;
function formatBullet(b) {
    var indent = '  '.repeat(Math.max(0, b.depth));
    var core = b.label;
    if (b.checkbox === 'open') {
        core = "[ ] ".concat(core);
    }
    else if (b.checkbox === 'done') {
        core = "[x] ".concat(core);
    }
    if (b.collapsed) {
        core = "".concat(core).concat(FOLD_SUFFIX);
    }
    return "".concat(indent, "- ").concat(core);
}
function serializeOutlineBody(doc) {
    return doc.lines
        .map(function (line) {
        if (line.kind === 'blank')
            return '';
        if (line.kind === 'text')
            return line.text;
        return formatBullet(line);
    })
        .join('\n');
}
exports.serializeOutlineBody = serializeOutlineBody;
/**
 * Build forest of bullets from flat lines (ignores text/blank for hierarchy;
 * text lines are not tree parents).
 */
function buildOutlineForest(doc) {
    var bullets = doc.lines.filter(function (l) { return l.kind === 'bullet'; });
    var roots = [];
    var stack = [];
    for (var _i = 0, bullets_1 = bullets; _i < bullets_1.length; _i++) {
        var b = bullets_1[_i];
        var node = { bullet: b, children: [] };
        while (stack.length && stack[stack.length - 1].bullet.depth >= b.depth) {
            stack.pop();
        }
        if (stack.length === 0) {
            roots.push(node);
        }
        else {
            stack[stack.length - 1].children.push(node);
        }
        stack.push(node);
    }
    return roots;
}
exports.buildOutlineForest = buildOutlineForest;
/**
 * Map a 0-based source line index in the raw body to the nearest bullet at or
 * above that line (blank/text lines inherit the previous bullet).
 */
function bulletIdAtSourceLine(doc, lineIndex) {
    if (!doc.lines.length)
        return null;
    var idx = Math.max(0, Math.min(lineIndex, doc.lines.length - 1));
    for (var i = idx; i >= 0; i--) {
        var line = doc.lines[i];
        if (line.kind === 'bullet') {
            return line.id;
        }
    }
    for (var i = idx + 1; i < doc.lines.length; i++) {
        var line = doc.lines[i];
        if (line.kind === 'bullet') {
            return line.id;
        }
    }
    return null;
}
exports.bulletIdAtSourceLine = bulletIdAtSourceLine;
/** Expand all ancestor bullets so `bulletId` can appear in flattenVisible. */
function expandAncestorsOf(doc, bulletId) {
    var bullets = doc.lines
        .map(function (l, i) { return ({ l: l, i: i }); })
        .filter(function (x) { return x.l.kind === 'bullet'; });
    var target = bullets.find(function (x) { return x.l.id === bulletId; });
    if (!target)
        return doc;
    var expandIds = new Set();
    var needDepth = target.l.depth - 1;
    for (var bi = bullets.length - 1; bi >= 0; bi--) {
        var b = bullets[bi];
        if (b.i >= target.i)
            continue;
        if (b.l.depth === needDepth) {
            if (b.l.collapsed) {
                expandIds.add(b.l.id);
            }
            needDepth -= 1;
            if (needDepth < 0)
                break;
        }
    }
    if (!expandIds.size)
        return doc;
    return {
        lines: doc.lines.map(function (line) {
            if (line.kind === 'bullet' && expandIds.has(line.id) && line.collapsed) {
                return __assign(__assign({}, line), { collapsed: false });
            }
            return line;
        }),
    };
}
exports.expandAncestorsOf = expandAncestorsOf;
/** Visible bullets in DFS order respecting collapsed. */
function flattenVisible(forest) {
    var out = [];
    var walk = function (nodes) {
        for (var _i = 0, nodes_1 = nodes; _i < nodes_1.length; _i++) {
            var n = nodes_1[_i];
            out.push(n);
            if (!n.bullet.collapsed && n.children.length) {
                walk(n.children);
            }
        }
    };
    walk(forest);
    return out;
}
exports.flattenVisible = flattenVisible;
function findBulletIndex(doc, id) {
    return doc.lines.findIndex(function (l) { return l.kind === 'bullet' && l.id === id; });
}
function toggleCollapse(doc, id) {
    var lines = doc.lines.map(function (l) {
        if (l.kind === 'bullet' && l.id === id) {
            return __assign(__assign({}, l), { collapsed: !l.collapsed });
        }
        return l;
    });
    return { lines: lines };
}
exports.toggleCollapse = toggleCollapse;
function setCollapsed(doc, id, collapsed) {
    var lines = doc.lines.map(function (l) {
        if (l.kind === 'bullet' && l.id === id) {
            return __assign(__assign({}, l), { collapsed: collapsed });
        }
        return l;
    });
    return { lines: lines };
}
exports.setCollapsed = setCollapsed;
/** Ids of bullets that have at least one deeper bullet after them (parents). */
function parentBulletIds(doc) {
    var parentIds = new Set();
    var bullets = doc.lines.filter(function (l) { return l.kind === 'bullet'; });
    for (var i = 0; i < bullets.length; i++) {
        var next = bullets[i + 1];
        if (next && next.depth > bullets[i].depth) {
            parentIds.add(bullets[i].id);
        }
    }
    return parentIds;
}
/**
 * Collapse or expand every parent bullet (has a deeper following bullet).
 * Collapse-all only marks parents; expand-all clears collapsed on all bullets.
 */
function setAllCollapsed(doc, collapsed) {
    if (!collapsed) {
        return {
            lines: doc.lines.map(function (l) {
                return l.kind === 'bullet' && l.collapsed ? __assign(__assign({}, l), { collapsed: false }) : l;
            }),
        };
    }
    var parentIds = parentBulletIds(doc);
    if (!parentIds.size)
        return doc;
    return {
        lines: doc.lines.map(function (l) {
            if (l.kind === 'bullet' && parentIds.has(l.id) && !l.collapsed) {
                return __assign(__assign({}, l), { collapsed: true });
            }
            return l;
        }),
    };
}
exports.setAllCollapsed = setAllCollapsed;
/**
 * Show outline through maxDepth (0-based indent depth). Deeper nodes stay folded.
 *
 * There is no virtual root node in the map:
 * - maxDepth 0 → only top-level bullets (presentation **level 2**)
 * - maxDepth 1 → top + one nested level (presentation **level 3**)
 */
function setExpandToMaxDepth(doc, maxDepth) {
    var d = Math.max(0, Math.floor(maxDepth));
    var parentIds = parentBulletIds(doc);
    return {
        lines: doc.lines.map(function (l) {
            if (l.kind !== 'bullet')
                return l;
            if (!parentIds.has(l.id)) {
                // Leaves: never need fold marker
                return l.collapsed ? __assign(__assign({}, l), { collapsed: false }) : l;
            }
            // Expand parents above the cut so children at `d` are visible; collapse at/after cut.
            var wantCollapsed = l.depth >= d;
            if (l.collapsed === wantCollapsed)
                return l;
            return __assign(__assign({}, l), { collapsed: wantCollapsed });
        }),
    };
}
exports.setExpandToMaxDepth = setExpandToMaxDepth;
/**
 * Presentation level (no root node): level 2 = first bullet tier only; level 3 = first+second.
 * `level` 2 → maxDepth 0; level 3 → maxDepth 1; … level 9 → maxDepth 7.
 * `level` ≤ 0 → collapse all parents (same as setAllCollapsed true).
 * `level` 1 → tops only (maxDepth 0), same cut as level 2.
 */
function setExpandToPresentationLevel(doc, level) {
    var lv = Math.floor(Number(level));
    if (!Number.isFinite(lv) || lv <= 0) {
        return setAllCollapsed(doc, true);
    }
    if (lv === 1) {
        return setExpandToMaxDepth(doc, 0);
    }
    var clamped = Math.min(9, Math.max(2, lv));
    return setExpandToMaxDepth(doc, clamped - 2);
}
exports.setExpandToPresentationLevel = setExpandToPresentationLevel;
function updateLabel(doc, id, label) {
    var lines = doc.lines.map(function (l) {
        if (l.kind === 'bullet' && l.id === id) {
            return __assign(__assign({}, l), { label: label });
        }
        return l;
    });
    return { lines: lines };
}
exports.updateLabel = updateLabel;
/**
 * Normalize multi-line map label editor draft to a single outline label line.
 */
function normalizeOutlineEditDraft(raw) {
    return String(raw || '')
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map(function (s) { return s.trim(); })
        .filter(function (s) { return s.length; })
        .join(' ');
}
exports.normalizeOutlineEditDraft = normalizeOutlineEditDraft;
/**
 * Parse map label edit draft:
 * - leading `[ ]` / `[x]` / `[X]` → checkbox state (like body parse)
 * - optional trailing fold ` (+)` stripped if typed
 * - remainder is label text (may still contain `<t: id>` tags)
 *
 * When no checkbox marker is present, `checkbox` is undefined (keep previous state).
 */
function parseOutlineEditDraft(raw) {
    var work = normalizeOutlineEditDraft(raw);
    if (FOLD_RE.test(work)) {
        work = work.replace(FOLD_RE, '').replace(/\s+$/, '');
    }
    // Leading task marker; space after ] optional so "[x]done" still works.
    var cb = /^\[([ xX])\]\s*(.*)$/.exec(work);
    if (cb) {
        var mark = cb[1];
        return {
            label: cb[2] || '',
            checkbox: mark === 'x' || mark === 'X' ? 'done' : 'open',
        };
    }
    return { label: work, checkbox: undefined };
}
exports.parseOutlineEditDraft = parseOutlineEditDraft;
/** Update bullet label and optionally checkbox from a map label edit. */
function updateBulletFromEdit(doc, id, label, checkbox) {
    var lines = doc.lines.map(function (l) {
        if (l.kind !== 'bullet' || l.id !== id)
            return l;
        if (checkbox === undefined) {
            return __assign(__assign({}, l), { label: label });
        }
        return __assign(__assign({}, l), { label: label, checkbox: checkbox });
    });
    return { lines: lines };
}
exports.updateBulletFromEdit = updateBulletFromEdit;
/**
 * Delimiter for slicing one bullet label into peer bullets (same depth).
 * Prefer explicit `,,,` over triple-space — spaces are too easy to type by accident.
 */
exports.PEER_SPLIT_MARKER = ',,,';
/**
 * Split a bullet's label on `,,,` (or custom sep) into same-depth peers.
 * First segment keeps the original bullet id + any children; further segments
 * become empty new siblings after the original block. No-op if fewer than two
 * non-empty parts.
 */
function splitBulletLabelIntoPeers(doc, id, sep) {
    if (sep === void 0) { sep = exports.PEER_SPLIT_MARKER; }
    var idx = findBulletIndex(doc, id);
    if (idx < 0 || !sep) {
        return { doc: doc, focusId: id, count: 0 };
    }
    var cur = doc.lines[idx];
    if (!cur.label.includes(sep)) {
        return { doc: doc, focusId: id, count: 0 };
    }
    var parts = cur.label
        .split(sep)
        .map(function (s) { return s.trim(); })
        .filter(function (s) { return s.length > 0; });
    if (parts.length < 2) {
        return { doc: doc, focusId: id, count: 0 };
    }
    // End of original bullet + descendants (same range as addSiblingAfter insert).
    var end = idx + 1;
    while (end < doc.lines.length) {
        var l = doc.lines[end];
        if (l.kind === 'bullet' && l.depth > cur.depth) {
            end += 1;
            continue;
        }
        break;
    }
    var head = __assign(__assign({}, cur), { label: parts[0] });
    var peers = parts.slice(1).map(function (label) { return ({
        kind: 'bullet',
        id: nextId('n'),
        depth: cur.depth,
        label: label,
        collapsed: false,
        checkbox: null,
    }); });
    var lines = __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], doc.lines.slice(0, idx), true), [
        head
    ], false), doc.lines.slice(idx + 1, end), true), peers, true), doc.lines.slice(end), true);
    return {
        doc: { lines: lines },
        focusId: peers[peers.length - 1].id,
        count: parts.length,
    };
}
exports.splitBulletLabelIntoPeers = splitBulletLabelIntoPeers;
/** Insert sibling after node (same depth). Returns new doc + new bullet id. */
function addSiblingAfter(doc, id) {
    var idx = findBulletIndex(doc, id);
    if (idx < 0) {
        return { doc: doc, newId: id };
    }
    var cur = doc.lines[idx];
    // Insert after this bullet and its descendants (deeper bullets)
    var insertAt = idx + 1;
    while (insertAt < doc.lines.length) {
        var l = doc.lines[insertAt];
        if (l.kind === 'bullet' && l.depth > cur.depth) {
            insertAt += 1;
            continue;
        }
        break;
    }
    var nb = {
        kind: 'bullet',
        id: nextId('n'),
        depth: cur.depth,
        label: '',
        collapsed: false,
        checkbox: null,
    };
    var lines = __spreadArray(__spreadArray(__spreadArray([], doc.lines.slice(0, insertAt), true), [nb], false), doc.lines.slice(insertAt), true);
    return { doc: { lines: lines }, newId: nb.id };
}
exports.addSiblingAfter = addSiblingAfter;
/** Insert first child under node (depth+1). Expands parent. */
function addChildUnder(doc, id) {
    var idx = findBulletIndex(doc, id);
    if (idx < 0) {
        return { doc: doc, newId: id };
    }
    var cur = doc.lines[idx];
    var lines = doc.lines.map(function (l, i) {
        if (i === idx && l.kind === 'bullet') {
            return __assign(__assign({}, l), { collapsed: false });
        }
        return l;
    });
    var nb = {
        kind: 'bullet',
        id: nextId('n'),
        depth: cur.depth + 1,
        label: '',
        collapsed: false,
        checkbox: null,
    };
    var next = __spreadArray(__spreadArray(__spreadArray([], lines.slice(0, idx + 1), true), [nb], false), lines.slice(idx + 1), true);
    return { doc: { lines: next }, newId: nb.id };
}
exports.addChildUnder = addChildUnder;
/** Indent: depth+1 if previous bullet allows (targetDepth <= prev.depth + 1). */
function indentBullet(doc, id) {
    var idx = findBulletIndex(doc, id);
    if (idx < 0)
        return doc;
    var cur = doc.lines[idx];
    var prev = null;
    for (var i = idx - 1; i >= 0; i--) {
        var l = doc.lines[i];
        if (l.kind === 'bullet') {
            prev = l;
            break;
        }
    }
    if (!prev)
        return doc;
    var targetDepth = cur.depth + 1;
    if (targetDepth > prev.depth + 1)
        return doc;
    return shiftDepthBlock(doc, idx, 1);
}
exports.indentBullet = indentBullet;
/** Outdent: decrease depth by 1 for node and descendants. */
function outdentBullet(doc, id) {
    var idx = findBulletIndex(doc, id);
    if (idx < 0)
        return doc;
    var cur = doc.lines[idx];
    if (cur.depth <= 0)
        return doc;
    return shiftDepthBlock(doc, idx, -1);
}
exports.outdentBullet = outdentBullet;
/**
 * Inclusive-exclusive range for a bullet and its descendant bullets
 * (and intervening non-bullet lines), stopping at the next sibling/uncle.
 */
function bulletBlockRange(doc, startIdx) {
    var cur = doc.lines[startIdx];
    if (!cur || cur.kind !== 'bullet') {
        return { start: startIdx, end: startIdx + 1 };
    }
    var baseDepth = cur.depth;
    var end = startIdx + 1;
    while (end < doc.lines.length) {
        var l = doc.lines[end];
        if (l.kind === 'bullet' && l.depth <= baseDepth) {
            break;
        }
        end += 1;
    }
    return { start: startIdx, end: end };
}
exports.bulletBlockRange = bulletBlockRange;
/** Previous sibling at the same depth, or -1. */
function findPrevSiblingIndex(doc, idx) {
    var cur = doc.lines[idx];
    for (var i = idx - 1; i >= 0; i--) {
        var l = doc.lines[i];
        if (l.kind !== 'bullet')
            continue;
        if (l.depth < cur.depth)
            return -1;
        if (l.depth === cur.depth)
            return i;
    }
    return -1;
}
/** Next sibling at the same depth, or -1. */
function findNextSiblingIndex(doc, idx) {
    var cur = doc.lines[idx];
    var end = bulletBlockRange(doc, idx).end;
    for (var i = end; i < doc.lines.length; i++) {
        var l = doc.lines[i];
        if (l.kind !== 'bullet')
            continue;
        if (l.depth < cur.depth)
            return -1;
        if (l.depth === cur.depth)
            return i;
    }
    return -1;
}
/**
 * Move bullet + descendants up among siblings (swap with previous sibling block).
 * No-op if already first among siblings.
 */
function moveBulletUp(doc, id) {
    var idx = findBulletIndex(doc, id);
    if (idx < 0)
        return doc;
    var prev = findPrevSiblingIndex(doc, idx);
    if (prev < 0)
        return doc;
    var a = bulletBlockRange(doc, prev);
    var b = bulletBlockRange(doc, idx);
    var before = doc.lines.slice(0, a.start);
    var blockA = doc.lines.slice(a.start, a.end);
    var mid = doc.lines.slice(a.end, b.start);
    var blockB = doc.lines.slice(b.start, b.end);
    var after = doc.lines.slice(b.end);
    return { lines: __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], before, true), blockB, true), mid, true), blockA, true), after, true) };
}
exports.moveBulletUp = moveBulletUp;
/**
 * Move bullet + descendants down among siblings (swap with next sibling block).
 * No-op if already last among siblings.
 */
function moveBulletDown(doc, id) {
    var idx = findBulletIndex(doc, id);
    if (idx < 0)
        return doc;
    var next = findNextSiblingIndex(doc, idx);
    if (next < 0)
        return doc;
    // Moving next sibling up is symmetric and keeps focus id stable.
    return moveBulletUp(doc, doc.lines[next].id);
}
exports.moveBulletDown = moveBulletDown;
function shiftDepthBlock(doc, startIdx, delta) {
    var cur = doc.lines[startIdx];
    var baseDepth = cur.depth;
    var out = [];
    var inBlock = false;
    for (var i = 0; i < doc.lines.length; i++) {
        var l = doc.lines[i];
        if (i === startIdx && l.kind === 'bullet') {
            inBlock = true;
            out.push(__assign(__assign({}, l), { depth: Math.max(0, l.depth + delta) }));
            continue;
        }
        if (inBlock && l.kind === 'bullet' && l.depth > baseDepth) {
            out.push(__assign(__assign({}, l), { depth: Math.max(0, l.depth + delta) }));
            continue;
        }
        if (inBlock && l.kind === 'bullet' && l.depth <= baseDepth) {
            inBlock = false;
        }
        out.push(l);
    }
    return { lines: out };
}
/** Delete bullet if empty label; promote children up one level. */
function deleteBulletIfEmpty(doc, id) {
    var idx = findBulletIndex(doc, id);
    if (idx < 0)
        return { doc: doc, focusId: null };
    var cur = doc.lines[idx];
    if (cur.label.trim() !== '') {
        return { doc: doc, focusId: id };
    }
    var prevId = null;
    for (var i = idx - 1; i >= 0; i--) {
        if (doc.lines[i].kind === 'bullet') {
            prevId = doc.lines[i].id;
            break;
        }
    }
    var baseDepth = cur.depth;
    var rebuilt = [];
    var after = false;
    for (var i = 0; i < doc.lines.length; i++) {
        var l = doc.lines[i];
        if (i === idx) {
            after = true;
            continue;
        }
        if (after && l.kind === 'bullet' && l.depth > baseDepth) {
            rebuilt.push(__assign(__assign({}, l), { depth: Math.max(0, l.depth - 1) }));
            continue;
        }
        if (after && l.kind === 'bullet' && l.depth <= baseDepth) {
            after = false;
        }
        rebuilt.push(l);
    }
    return { doc: { lines: rebuilt }, focusId: prevId };
}
exports.deleteBulletIfEmpty = deleteBulletIfEmpty;
function toggleCheckbox(doc, id) {
    var lines = doc.lines.map(function (l) {
        if (l.kind !== 'bullet' || l.id !== id)
            return l;
        var checkbox = l.checkbox;
        if (checkbox === null)
            checkbox = 'open';
        else if (checkbox === 'open')
            checkbox = 'done';
        else
            checkbox = null;
        return __assign(__assign({}, l), { checkbox: checkbox });
    });
    return { lines: lines };
}
exports.toggleCheckbox = toggleCheckbox;
